import type { Tab } from './index';
import { esc, filterTable } from '../utils';
import { fetchSheet, workerFetch, QUIVER_KEY } from '../worker';
import { writeToSheet } from '../sheets';
import { showToast } from '../toast';
import { addTickerToWatchlist } from '../watchlist';

interface PoliticalRow {
  _id?:         string | number;
  Person?:      string;
  PersonColor?: string;
  Role?:        string;
  Ticker?:      string;
  TickerURL?:   string;
  AssetName?:   string;
  ApproxValue?: string;
  ValueColor?:  string;
  Notes?:       string;
}

interface CongressTrade {
  Name?:          string;
  Party?:         string;
  Chamber?:       string;
  Ticker?:        string;
  Transaction?:   string;
  Min?:           number;
  Max?:           number;
  Amount?:        string;
  Date?:          string;
  ReportDate?:    string;
  Range?:         string;
  ExcessReturn?:  number;
  Description?:   string;
}

type PanelView = 'holdings' | 'congress' | 'performance';

const PAGE_SIZE = 50;

const PARTY_COLORS: Record<string, string> = {
  D: '#60a5fa',
  R: '#f87171',
  I: '#a78bfa',
};

function fmtAmount(trade: CongressTrade): string {
  if (trade.Range) return trade.Range;
  if (trade.Min != null) {
    const fmt = (n: number) => n >= 1_000_000 ? `$${(n/1_000_000).toFixed(1)}M` : `$${(n/1000).toFixed(0)}K`;
    return trade.Max ? `${fmt(trade.Min)}–${fmt(trade.Max)}` : `${fmt(trade.Min)}+`;
  }
  if (trade.Amount) {
    const n = parseFloat(String(trade.Amount));
    if (!isNaN(n) && n > 0) {
      return '$' + Math.round(n).toLocaleString('en-US');
    }
  }
  return '—';
}

export class PoliticalTab implements Tab {
  readonly id = 'political';

  private loaded      = false;
  private activeView: PanelView = 'holdings';
  private allTrades: CongressTrade[] = [];
  private filteredTrades: CongressTrade[] = [];
  private currentPage = 1;

  private el(id: string): HTMLElement { return document.getElementById(id) as HTMLElement; }

  init(): void {
    // Holdings filters
    (this.el('political-filter') as HTMLInputElement)
      .addEventListener('input', e => filterTable('political-table', (e.target as HTMLInputElement).value));
    (this.el('political-person-filter') as HTMLSelectElement)
      .addEventListener('change', e => this.filterByPerson((e.target as HTMLSelectElement).value));

    // Delete via event delegation
    this.el('political-tbody').addEventListener('click', e => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-delete-id]');
      if (btn) this.deleteHolding(btn.dataset.deleteId ?? '');
    });

    // Sub-tab toggle
    this.el('pol-tab-holdings').addEventListener('click',    () => this.showPanel('holdings'));
    this.el('pol-tab-congress').addEventListener('click',    () => this.showPanel('congress'));
    this.el('pol-tab-performance').addEventListener('click', () => this.showPanel('performance'));

    // Congress filters
    const refilter = () => { this.applyCongressFilters(); this.currentPage = 1; this.renderCongress(); };
    this.el('cong-party-filter').addEventListener('change',   refilter);
    this.el('cong-chamber-filter').addEventListener('change', refilter);
    this.el('cong-type-filter').addEventListener('change',    refilter);
    (this.el('cong-name-filter')   as HTMLInputElement).addEventListener('input', refilter);
    (this.el('cong-ticker-filter') as HTMLInputElement).addEventListener('input', refilter);

    // Congress chart links + watchlist — event delegation
    this.el('cong-tbody').addEventListener('click', e => {
      const chartBtn = (e.target as HTMLElement).closest<HTMLElement>('[data-chart-ticker]');
      if (chartBtn) {
        (window as any).showTab?.('charts');
        (window as any).loadChartTicker?.(chartBtn.dataset.chartTicker);
        return;
      }
      const watchBtn = (e.target as HTMLElement).closest<HTMLElement>('[data-watch-ticker]');
      if (watchBtn) {
        const ticker = watchBtn.dataset.watchTicker ?? '';
        addTickerToWatchlist(ticker);
        watchBtn.textContent = '✓';
        watchBtn.style.color = 'var(--green)';
        watchBtn.style.borderColor = 'var(--green)';
        showToast(`${ticker} added to watchlist`);
      }
    });
  }

  render(): void {
    if (!this.loaded) this.loadHoldings();
    if (this.activeView === 'congress' && !this.allTrades.length) this.loadCongress();
  }

  // ── Sub-tab toggle ────────────────────────────────────────────────────────

  private showPanel(view: PanelView): void {
    this.activeView = view;

    this.el('pol-panel-holdings').style.display    = view === 'holdings'    ? '' : 'none';
    this.el('pol-panel-congress').style.display    = view === 'congress'    ? '' : 'none';
    this.el('pol-panel-performance').style.display = view === 'performance' ? '' : 'none';

    const active = (on: boolean) => ({
      background:  on ? 'rgba(0,212,255,0.15)' : 'transparent',
      borderColor: on ? 'rgba(0,212,255,0.4)'  : 'var(--border)',
      color:       on ? 'var(--accent)'         : 'var(--text3)',
    });
    const applyStyle = (el: HTMLElement, s: { background: string; borderColor: string; color: string }) => {
      el.style.background = s.background; el.style.borderColor = s.borderColor; el.style.color = s.color;
    };
    applyStyle(this.el('pol-tab-holdings'),    active(view === 'holdings'));
    applyStyle(this.el('pol-tab-congress'),    active(view === 'congress'));
    applyStyle(this.el('pol-tab-performance'), active(view === 'performance'));

    if (view === 'congress' && !this.allTrades.length) this.loadCongress();
    if (view === 'performance') this.renderPerformance();
  }

  // ── Holdings panel ─────────────────────────────────────────────────────────

  private async loadHoldings(): Promise<void> {
    const tbody = this.el('political-tbody');
    tbody.innerHTML = `<tr><td colspan="7" style="text-align:center;color:var(--text3);padding:24px;letter-spacing:0.1em;font-size:11px;">LOADING…</td></tr>`;
    try {
      const data = await fetchSheet<PoliticalRow>('Political');
      const rows = data.rows ?? [];
      if (!rows.length) {
        tbody.innerHTML = `<tr><td colspan="7" style="text-align:center;color:var(--text3);padding:24px;font-size:12px;">No data found in Political sheet tab.</td></tr>`;
        return;
      }
      tbody.innerHTML = rows.map(r => this.buildHoldingRow(r)).join('');
      this.loaded = true;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      tbody.innerHTML = `<tr><td colspan="7" style="text-align:center;padding:24px;"><span style="color:var(--red);font-size:12px;">Could not load Political Holdings.</span><br><span style="color:var(--text3);font-size:10px;">${esc(msg)}</span></td></tr>`;
    }
  }

  private buildHoldingRow(r: PoliticalRow): string {
    const pc    = esc(r.PersonColor ?? 'text-muted');
    const vc    = r.ValueColor === 'warn' ? 'warn' : 'text-muted';
    const badge = r.TickerURL
      ? `<span class="ticker-badge"><a href="${esc(r.TickerURL)}" target="_blank" rel="noopener">${esc(r.Ticker ?? '')}</a></span>`
      : `<span class="ticker-badge">${esc(r.Ticker ?? '')}</span>`;
    const chartBtn = r.Ticker
      ? ` <button data-chart-ticker="${esc(r.Ticker)}" title="Chart ${esc(r.Ticker)}" style="background:rgba(0,212,255,0.08);border:1px solid rgba(0,212,255,0.2);color:var(--accent);font-family:var(--mono);font-size:10px;padding:1px 5px;cursor:pointer;border-radius:2px;vertical-align:middle;">📈</button>`
      : '';
    const delBtn = r._id != null
      ? `<button data-delete-id="${esc(String(r._id))}" style="background:rgba(255,71,87,0.08);border:1px solid rgba(255,71,87,0.2);color:var(--red);font-family:var(--mono);font-size:10px;padding:2px 7px;cursor:pointer;border-radius:2px;">🗑️</button>`
      : '';

    return `<tr>
      <td class="${pc}"><strong>${esc(r.Person ?? '—')}</strong></td>
      <td class="text-muted">${esc(r.Role ?? '—')}</td>
      <td>${badge}${chartBtn}</td>
      <td>${esc(r.AssetName ?? '—')}</td>
      <td class="${vc}">${esc(r.ApproxValue ?? '—')}</td>
      <td class="notes-cell">${esc(r.Notes ?? '—')}</td>
      <td>${delBtn}</td>
    </tr>`;
  }

  private async deleteHolding(id: string): Promise<void> {
    if (!id || !confirm('Delete this entry from Political Holdings?')) return;
    const ok = await writeToSheet('delete', 'Political', { _id: id });
    if (ok) { showToast('Entry deleted'); this.loaded = false; await this.loadHoldings(); }
  }

  private filterByPerson(val: string): void {
    document.querySelectorAll<HTMLTableRowElement>('#political-table tbody tr').forEach(row => {
      if (!val) { row.style.display = ''; return; }
      const person = row.cells[0]?.textContent ?? '';
      const role   = row.cells[1]?.textContent ?? '';
      if (val === 'Donald Trump') {
        row.style.display = person.includes('Donald Trump') && !person.includes('Jr') ? '' : 'none';
      } else if (val === 'Cabinet') {
        row.style.display = ['Sec.','Dir.','OMB'].some(r => role.includes(r)) ? '' : 'none';
      } else if (val === 'Family') {
        row.style.display = (role.includes('Son') || role.includes('Son-in-Law')) ? '' : 'none';
      }
    });
  }

  // ── Congress Trades panel ─────────────────────────────────────────────────

  private async loadCongress(): Promise<void> {
    const tbody  = this.el('cong-tbody');
    const status = this.el('cong-status');
    tbody.innerHTML = `<tr><td colspan="8" style="text-align:center;color:var(--text3);padding:30px;letter-spacing:0.1em;font-size:11px;">LOADING CONGRESSIONAL TRADES…</td></tr>`;
    status.textContent = 'Fetching from Quiver Quantitative…';

    const quiverKey = localStorage.getItem(QUIVER_KEY) ?? '';

    try {
      const headers: Record<string, string> = {};
      if (quiverKey) headers['X-Quiver-Key'] = quiverKey;
      const data = await workerFetch<{ trades: CongressTrade[]; count: number; cachedAt?: string; paid?: boolean }>(
        '/api/congress-trades',
        { headers, signal: AbortSignal.timeout(30000) },
      );
      if ((data as any).error) throw new Error((data as any).error);

      this.allTrades = data.trades ?? [];
      this.applyCongressFilters();
      this.currentPage = 1;
      this.renderCongress();

      const cached = data.cachedAt ? ` · cached ${new Date(data.cachedAt).toLocaleTimeString()}` : '';
      const src = data.paid ? '' : ' · public feed';
      status.textContent = `${data.count.toLocaleString()} trades${cached}${src}`;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      tbody.innerHTML = `<tr><td colspan="8" style="text-align:center;padding:30px;"><span style="color:var(--red);font-size:12px;">Could not load Congressional trades.</span><br><span style="color:var(--text3);font-size:11px;">${esc(msg)}</span></td></tr>`;
      status.textContent = 'Error';
    }
  }

  private applyCongressFilters(): void {
    const party   = (this.el('cong-party-filter')   as HTMLSelectElement).value;
    const chamber = (this.el('cong-chamber-filter') as HTMLSelectElement).value;
    const type    = (this.el('cong-type-filter')    as HTMLSelectElement).value;
    const name    = (this.el('cong-name-filter')    as HTMLInputElement).value.toLowerCase().trim();
    const ticker  = (this.el('cong-ticker-filter')  as HTMLInputElement).value.toUpperCase().trim();

    this.filteredTrades = this.allTrades.filter(t => {
      if (party  && t.Party       !== party)  return false;
      if (type   && t.Transaction !== type)   return false;
      if (ticker && (t.Ticker ?? '').toUpperCase() !== ticker) return false;
      if (name   && !(t.Name ?? '').toLowerCase().includes(name)) return false;
      if (chamber) {
        const c = (t.Chamber ?? '').toUpperCase();
        if (chamber === 'House'  && c !== 'H' && c !== 'HOUSE'  && c !== 'REPRESENTATIVES') return false;
        if (chamber === 'Senate' && c !== 'S' && c !== 'SENATE' && c !== 'SENATORS')        return false;
      }
      return true;
    });

    this.el('cong-count').textContent = this.filteredTrades.length.toLocaleString();
  }

  private renderCongress(): void {
    const tbody     = this.el('cong-tbody');
    const totalPages = Math.max(1, Math.ceil(this.filteredTrades.length / PAGE_SIZE));
    if (this.currentPage > totalPages) this.currentPage = totalPages;

    const start  = (this.currentPage - 1) * PAGE_SIZE;
    const slice  = this.filteredTrades.slice(start, start + PAGE_SIZE);

    if (!slice.length) {
      tbody.innerHTML = `<tr><td colspan="8" style="text-align:center;color:var(--text3);padding:30px;font-size:12px;">No trades match your filters.</td></tr>`;
      this.renderPagination(0);
      return;
    }

    tbody.innerHTML = slice.map(t => {
      const partyColor = PARTY_COLORS[t.Party ?? ''] ?? 'var(--text3)';
      const typeColor  = (t.Transaction ?? '').toLowerCase().includes('sale') ? 'var(--red)' : 'var(--green)';
      const cRaw = (t.Chamber ?? '').toUpperCase();
      const chamber = cRaw === 'H' || cRaw === 'HOUSE' || cRaw === 'REPRESENTATIVES' ? 'House'
                    : cRaw === 'S' || cRaw === 'SENATE' || cRaw === 'SENATORS'       ? 'Senate'
                    : (t.Chamber ?? '—');
      const chartBtn   = t.Ticker
        ? `<button data-chart-ticker="${esc(t.Ticker)}" title="Chart ${esc(t.Ticker)}" style="background:rgba(0,212,255,0.08);border:1px solid rgba(0,212,255,0.2);color:var(--accent);font-family:var(--mono);font-size:10px;padding:1px 5px;cursor:pointer;border-radius:2px;vertical-align:middle;margin-left:4px;">📈</button>`
        : '';
      const watchBtn = t.Ticker
        ? `<button data-watch-ticker="${esc(t.Ticker)}" title="Add ${esc(t.Ticker)} to watchlist" style="background:rgba(46,213,115,0.08);border:1px solid rgba(46,213,115,0.3);color:var(--green);font-size:11px;font-weight:700;padding:1px 7px;cursor:pointer;border-radius:2px;vertical-align:middle;margin-left:3px;">+W</button>`
        : '';

      return `<tr>
        <td style="font-size:12px;"><strong>${esc(t.Name ?? '—')}</strong></td>
        <td style="font-family:var(--mono);font-size:11px;font-weight:700;color:${partyColor};">${esc(t.Party ?? '—')}</td>
        <td style="font-size:11px;color:var(--text2);">${esc(chamber)}</td>
        <td style="white-space:nowrap;"><span class="ticker-badge">${esc(t.Ticker ?? '—')}</span>${chartBtn}${watchBtn}</td>
        <td style="font-family:var(--mono);font-size:11px;font-weight:700;color:${typeColor};">${esc(t.Transaction ?? '—')}</td>
        <td style="font-family:var(--mono);font-size:11px;color:var(--text2);">${esc(fmtAmount(t))}</td>
        <td style="font-family:var(--mono);font-size:11px;color:var(--text3);">${esc(t.Date ?? '—')}</td>
        <td style="font-family:var(--mono);font-size:11px;color:var(--text3);">${esc(t.ReportDate ?? '—')}</td>
      </tr>`;
    }).join('');

    this.renderPagination(totalPages);
  }

  private renderPagination(totalPages: number): void {
    const container = this.el('cong-pagination');
    if (totalPages <= 1) { container.innerHTML = ''; return; }

    const cur = this.currentPage;
    const pages: (number | '…')[] = [];

    // Always show first, last, current ±2, with ellipsis
    const add = (n: number) => { if (!pages.includes(n)) pages.push(n); };
    add(1);
    if (cur > 4) pages.push('…');
    for (let i = Math.max(2, cur - 2); i <= Math.min(totalPages - 1, cur + 2); i++) add(i);
    if (cur < totalPages - 3) pages.push('…');
    add(totalPages);

    const btnStyle = (active: boolean) =>
      `background:${active ? 'rgba(0,212,255,0.15)' : 'transparent'};border:1px solid ${active ? 'rgba(0,212,255,0.4)' : 'var(--border)'};color:${active ? 'var(--accent)' : 'var(--text3)'};font-family:var(--mono);font-size:11px;padding:4px 10px;cursor:${active ? 'default' : 'pointer'};border-radius:2px;transition:all 0.15s;`;

    container.innerHTML = `
      <div style="display:flex;gap:4px;align-items:center;justify-content:center;margin-top:12px;flex-wrap:wrap;">
        <button ${cur === 1 ? 'disabled' : ''} data-page="${cur - 1}" style="${btnStyle(false)}opacity:${cur === 1 ? '0.3' : '1'}">‹</button>
        ${pages.map(p => p === '…'
          ? `<span style="color:var(--text3);font-family:var(--mono);font-size:11px;padding:4px 6px;">…</span>`
          : `<button data-page="${p}" style="${btnStyle(p === cur)}">${p}</button>`
        ).join('')}
        <button ${cur === totalPages ? 'disabled' : ''} data-page="${cur + 1}" style="${btnStyle(false)}opacity:${cur === totalPages ? '0.3' : '1'}">›</button>
      </div>
      <div style="text-align:center;font-size:10px;color:var(--text3);font-family:var(--mono);margin-top:6px;">
        Page ${cur} of ${totalPages} · ${this.filteredTrades.length.toLocaleString()} trades
      </div>`;

    container.querySelectorAll<HTMLElement>('[data-page]').forEach(btn => {
      btn.addEventListener('click', () => {
        this.currentPage = Number(btn.dataset.page);
        this.renderCongress();
        this.el('pol-panel-congress').scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  // ── Performance panel ─────────────────────────────────────────────────────

  private renderPerformance(): void {
    const body = this.el('perf-body');
    if (!this.allTrades.length) {
      body.innerHTML = `<div style="padding:24px;text-align:center;color:var(--text3);font-family:var(--mono);font-size:12px;">Load Congress Trades first, then return here.</div>`;
      return;
    }

    // Only trades with meaningful ExcessReturn data
    const withData = this.allTrades.filter(t => t.ExcessReturn != null && t.Ticker && t.Name);
    const isBuy    = (t: CongressTrade) => !(t.Transaction ?? '').toLowerCase().includes('sale');
    const isWin    = (t: CongressTrade) =>
      isBuy(t) ? (t.ExcessReturn ?? 0) > 0   // bought before outperformance
               : (t.ExcessReturn ?? 0) < 0;  // sold before underperformance

    const wins  = withData.filter(isWin).length;
    const total = withData.length;
    const winPct = total > 0 ? Math.round(wins / total * 100) : 0;
    const avgAlpha = total > 0
      ? (withData.reduce((s, t) => s + (t.ExcessReturn ?? 0), 0) / total * 100).toFixed(1)
      : '—';
    const winCol = winPct >= 55 ? 'var(--green)' : winPct >= 45 ? 'var(--warn)' : 'var(--red)';

    // ── By politician ──────────────────────────────────────────────────────
    const polMap: Record<string, { wins: number; total: number; alpha: number; party: string }> = {};
    withData.forEach(t => {
      const n = t.Name ?? '?';
      if (!polMap[n]) polMap[n] = { wins: 0, total: 0, alpha: 0, party: t.Party ?? '' };
      polMap[n].total++;
      polMap[n].alpha += (t.ExcessReturn ?? 0);
      if (isWin(t)) polMap[n].wins++;
    });
    const politicians = Object.entries(polMap)
      .filter(([, v]) => v.total >= 3)
      .map(([name, v]) => ({ name, ...v, winPct: Math.round(v.wins / v.total * 100), avgAlpha: v.alpha / v.total }))
      .sort((a, b) => b.winPct - a.winPct || b.avgAlpha - a.avgAlpha);

    const polRows = politicians.slice(0, 25).map(p => {
      const pc = PARTY_COLORS[p.party] ?? 'var(--text3)';
      const wc = p.winPct >= 55 ? 'var(--green)' : p.winPct >= 45 ? 'var(--warn)' : 'var(--red)';
      const ac = p.avgAlpha >= 0 ? 'var(--green)' : 'var(--red)';
      const alphaStr = (p.avgAlpha * 100 >= 0 ? '+' : '') + (p.avgAlpha * 100).toFixed(1) + '%';
      return `<tr style="border-bottom:1px solid var(--border);">
        <td style="padding:8px 12px;font-size:12px;">${esc(p.name)}</td>
        <td style="padding:8px 12px;font-family:var(--mono);font-size:11px;font-weight:700;color:${pc};">${esc(p.party)}</td>
        <td style="padding:8px 12px;font-family:var(--mono);font-size:13px;font-weight:700;color:${wc};">${p.winPct}%</td>
        <td style="padding:8px 12px;font-family:var(--mono);font-size:12px;color:${ac};">${alphaStr}</td>
        <td style="padding:8px 12px;font-family:var(--mono);font-size:11px;color:var(--text3);">${p.total}</td>
      </tr>`;
    }).join('');

    // ── By ticker ──────────────────────────────────────────────────────────
    const tickMap: Record<string, { wins: number; total: number; alpha: number }> = {};
    withData.forEach(t => {
      const tk = t.Ticker ?? '?';
      if (!tickMap[tk]) tickMap[tk] = { wins: 0, total: 0, alpha: 0 };
      tickMap[tk].total++;
      tickMap[tk].alpha += (t.ExcessReturn ?? 0);
      if (isWin(t)) tickMap[tk].wins++;
    });
    const tickers = Object.entries(tickMap)
      .filter(([, v]) => v.total >= 2)
      .map(([ticker, v]) => ({ ticker, ...v, winPct: Math.round(v.wins / v.total * 100), avgAlpha: v.alpha / v.total }))
      .sort((a, b) => b.winPct - a.winPct || b.avgAlpha - a.avgAlpha);

    const tickRows = tickers.slice(0, 20).map(tk => {
      const wc = tk.winPct >= 55 ? 'var(--green)' : tk.winPct >= 45 ? 'var(--warn)' : 'var(--red)';
      const ac = tk.avgAlpha >= 0 ? 'var(--green)' : 'var(--red)';
      const alphaStr = (tk.avgAlpha * 100 >= 0 ? '+' : '') + (tk.avgAlpha * 100).toFixed(1) + '%';
      return `<tr style="border-bottom:1px solid var(--border);">
        <td style="padding:8px 12px;font-family:var(--mono);font-size:13px;font-weight:700;color:var(--accent);">${esc(tk.ticker)}</td>
        <td style="padding:8px 12px;font-family:var(--mono);font-size:13px;font-weight:700;color:${wc};">${tk.winPct}%</td>
        <td style="padding:8px 12px;font-family:var(--mono);font-size:12px;color:${ac};">${alphaStr}</td>
        <td style="padding:8px 12px;font-family:var(--mono);font-size:11px;color:var(--text3);">${tk.total}</td>
      </tr>`;
    }).join('');

    const thStyle = 'padding:6px 12px;text-align:left;font-size:10px;color:var(--text3);text-transform:uppercase;letter-spacing:0.1em;';

    body.innerHTML = `
      <!-- Summary cards -->
      <div style="display:flex;gap:12px;flex-wrap:wrap;margin-bottom:20px;">
        ${this.perfCard('Win Rate', `${winPct}%`, winCol, 'Trades where the politician\'s timing beat SPY')}
        ${this.perfCard('Avg Alpha', `${avgAlpha}%`, parseFloat(String(avgAlpha)) >= 0 ? 'var(--green)' : 'var(--red)', 'Avg excess return vs SPY per trade')}
        ${this.perfCard('Trades Analyzed', total.toLocaleString(), 'var(--accent)', 'Trades with ExcessReturn data from Quiver')}
        ${this.perfCard('Politicians', politicians.length.toString(), 'var(--text2)', 'With 3+ trades in dataset')}
      </div>

      <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px;align-items:start;">
        <!-- By politician -->
        <div>
          <div style="font-family:var(--mono);font-size:10px;font-weight:700;color:var(--text2);letter-spacing:0.1em;text-transform:uppercase;margin-bottom:8px;">Top Timers — By Politician</div>
          <div style="font-size:10px;color:var(--text3);margin-bottom:10px;">Min 3 trades · Win = beat SPY · Sorted by win rate</div>
          <table style="width:100%;border-collapse:collapse;font-size:12px;">
            <thead><tr style="border-bottom:2px solid var(--border);">
              <th style="${thStyle}Politician</th>
              <th style="${thStyle}Party</th>
              <th style="${thStyle}Win %</th>
              <th style="${thStyle}Avg Alpha</th>
              <th style="${thStyle}Trades</th>
            </tr></thead>
            <tbody>${polRows || '<tr><td colspan="5" style="padding:16px;text-align:center;color:var(--text3);font-size:11px;">Not enough data</td></tr>'}</tbody>
          </table>
        </div>

        <!-- By ticker -->
        <div>
          <div style="font-family:var(--mono);font-size:10px;font-weight:700;color:var(--text2);letter-spacing:0.1em;text-transform:uppercase;margin-bottom:8px;">Top Picks — By Ticker</div>
          <div style="font-size:10px;color:var(--text3);margin-bottom:10px;">Min 2 trades · All transactions · Sorted by win rate</div>
          <table style="width:100%;border-collapse:collapse;font-size:12px;">
            <thead><tr style="border-bottom:2px solid var(--border);">
              <th style="${thStyle}Ticker</th>
              <th style="${thStyle}Win %</th>
              <th style="${thStyle}Avg Alpha</th>
              <th style="${thStyle}Trades</th>
            </tr></thead>
            <tbody>${tickRows || '<tr><td colspan="4" style="padding:16px;text-align:center;color:var(--text3);font-size:11px;">Not enough data</td></tr>'}</tbody>
          </table>
        </div>
      </div>

      <div style="margin-top:16px;padding:10px 14px;background:rgba(122,157,181,0.05);border:1px solid var(--border);border-radius:3px;font-size:10px;color:var(--text3);line-height:1.6;">
        ℹ ExcessReturn = stock return minus SPY return since trade date (from Quiver Quant). Win = beat market: BUY before outperformance, or SALE before underperformance. Shown on ${total.toLocaleString()} of ${this.allTrades.length.toLocaleString()} total loaded trades.
      </div>`;
  }

  private perfCard(label: string, value: string, color: string, hint: string): string {
    return `<div style="background:var(--panel);border:1px solid var(--border);border-radius:4px;padding:14px 18px;flex:1;min-width:140px;" title="${esc(hint)}">
      <div style="font-size:10px;color:var(--text3);letter-spacing:0.1em;text-transform:uppercase;margin-bottom:6px;">${esc(label)}</div>
      <div style="font-family:var(--mono);font-size:22px;font-weight:700;color:${color};">${esc(value)}</div>
    </div>`;
  }
}
